import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, Link, PaperProps, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import moment from 'moment';

import { AnalyticsProfile, useGetOiCtaPeopleQuery } from 'api';
import { useNotify } from 'hooks';
import { Insights, Loaders, PaddedPaper } from 'components';
import theme from 'theme';

import { getTitles } from 'features/insights/helpers';
import { getAvatarSrc } from 'helpers';

interface Props {
  elevation: PaperProps['elevation'];
}

const flair = [
  {
    color: '#EA1D64',
    text: 'New Lead 🙋‍',
  },
  {
    color: '#F9A81A',
    text: 'Customer 👑',
  },
  {
    color: '#6FBE44',
    text: 'Target Account 🎯',
  },
  {
    color: '#5677FC',
    text: 'Partner 🧑‍🤝‍🧑',
  },
  {
    color: '#E62A10',
    text: 'Hiring Candidate 🏆',
  },
];

const styles = {
  pill: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: 16,
    font: theme.typography.pxToRem(12),
  },
} as const;

const OICTAWidget: FC<Props> = ({ elevation }) => {
  const notify = useNotify();
  const { t } = useTranslation();
  const [startTime] = useState(moment().subtract('1', 'year').unix());
  const [endTime] = useState(moment().unix());

  const { data: peopleData, isLoading: isPeopleLoading } = useGetOiCtaPeopleQuery(
    {
      start: startTime,
      end: endTime,
      limit: 5,
    },
    {
      onError: notify.queryError,
    }
  );

  if (isPeopleLoading) {
    return <Loaders.Widget />;
  }

  const { results = [] } = peopleData?.getPeople || {};

  function renderPerson(profile: AnalyticsProfile, index: number) {
    const titles = getTitles(profile);

    return (
      <Grid
        xs={12}
        container
        alignItems="center"
        justifyContent="center"
        marginBottom={2}
        key={index}
      >
        <Grid marginRight={1}>
          <Link
            display="block"
            underline="none"
            target="_blank"
            title={profile.name}
            href={profile.network_profile_url}
          >
            <Avatar
              src={getAvatarSrc(profile.network_profile_image, 40)}
              style={{ height: 40, width: 40 }}
            />
          </Link>
        </Grid>
        <Grid xs>
          <Link
            noWrap
            display="block"
            underline="hover"
            variant="subtitle2"
            target="_blank"
            title={profile.name}
            href={profile.network_profile_url}
          >
            {profile.name}
          </Link>
          <Typography noWrap title={titles}>
            {titles}
          </Typography>
        </Grid>
        {!elevation && (
          <Grid display="inline-flex" paddingRight={1} paddingLeft={1}>
            <div style={{ backgroundColor: flair[index].color, ...styles.pill }}>
              <Typography variant="overline" color="common.white">
                {flair[index].text}
              </Typography>
            </div>
          </Grid>
        )}
        <Grid display="inline-flex" paddingRight={1}>
          <Avatar
            variant="square"
            src={profile.company.company_logo}
            style={{ height: 40, width: 40, borderRadius: 4 }}
          />
        </Grid>
      </Grid>
    );
  }

  return (
    <PaddedPaper elevation={elevation}>
      <Box paddingBottom={2}>
        <Insights.IntelligenceHeader variant="small" />
      </Box>
      {!!results.length && results.map(renderPerson)}
      <Box textAlign="center" paddingTop={2}>
        <Insights.CTAButton text={t('insights:abm_widget_cta')} variant="small" />
      </Box>
    </PaddedPaper>
  );
};

export default OICTAWidget;
